<!-- src/apps/secret/reveal/UnknownSecret.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import BaseUnknownSecret from '@/shared/components/base/BaseUnknownSecret.vue';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import UnknownSecretHelpContent from '@/apps/secret/components/UnknownSecretHelpContent.vue';
  import type { BrandSettings } from '@/schemas/models/domain/brand';

const { t } = useI18n();

  interface Props {
    branded?: boolean;
    brandSettings?: BrandSettings;
  }

  defineProps<Props>();
</script>

<template>
  <BaseUnknownSecret
    :branded="branded"
    :brand-settings="brandSettings">
    <template #header="{}">
      <div class="py-12 text-center">
        <!-- Icon -->
        <div class="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
          <svg
            class="h-8 w-8 text-red-600 dark:text-red-400"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-labelledby="secret-not-available-icon-title">
            <title id="secret-not-available-icon-title">{{ t("web.secrets.that_information_is_no_longer_available") }}</title>
            <path
              d="M10 14L12 12M12 12L14 10M12 12L10 10M12 12L14 14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </div>

        <!-- Title -->
        <h1 class="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
          {{ t("web.secrets.that_information_is_no_longer_available") }}
        </h1>

        <!-- Help Link -->
        <div class="flex items-center justify-center gap-2">
          <NeedHelpModal
            link-icon-name="question-mark-circle-16-solid"
            link-text-label=""
            aria-haspopup="dialog">
            <button
              type="button"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200">
              {{ t('web.COMMON.need_help') }}
            </button>
            <template #content>
              <UnknownSecretHelpContent />
            </template>
          </NeedHelpModal>
        </div>
      </div>
    </template>

    <template #message="{}">
    </template>

    <template #alert="{}">
    </template>

    <template #action="{}">
      <!-- Simple action buttons -->
      <div class="flex flex-col gap-3 sm:flex-row">
        <!-- Return to Home Button -->
        <router-link
          to="/"
          class="inline-flex items-center justify-center rounded-lg border-2 border-brand-600 bg-white px-6 py-2.5 font-semibold text-brand-600 transition-colors hover:bg-brand-50 dark:border-brand-500 dark:bg-gray-800 dark:text-brand-400 dark:hover:bg-gray-700"
          :aria-label="t('web.layout.return_to_home_page')">
          {{ t('web.layout.return_to_home') }}
        </router-link>

        <!-- Create a Secret Button -->
        <router-link
          to="/"
          class="inline-flex items-center justify-center rounded-lg bg-brand-600 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-brand-700 dark:bg-brand-700 dark:hover:bg-brand-600"
          :aria-label="t('web.secrets.create_a_secret')">
          {{ t('web.secrets.create_a_secret') }}
        </router-link>
      </div>
    </template>
  </BaseUnknownSecret>
</template>
